/**
 * Where each module ends up on the canvas. Same arithmetic as CSS flex with `min-*: 0`: fixed sizes
 * first, the rest shared by `fr` weight (a child without a size counts as `1fr`).
 */
import { CANVAS, type LayoutNode, type Size } from './model';

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function boxesOf(root: LayoutNode, canvas: Box = { x: 0, y: 0, ...CANVAS }): Map<string, Box> {
  const out = new Map<string, Box>();
  place(root, canvas, out);
  return out;
}

function place(node: LayoutNode, box: Box, out: Map<string, Box>): void {
  if (node.kind === 'slot') {
    out.set(node.module, box);
    return;
  }
  const horizontal = node.direction === 'row';
  const total = horizontal ? box.width : box.height;
  const lengths = shareOut(node.children.map((c) => c.size), total);
  let offset = 0;
  node.children.forEach((child, i) => {
    const len = lengths[i] ?? 0;
    const childBox: Box = horizontal
      ? { x: box.x + offset, y: box.y, width: len, height: box.height }
      : { x: box.x, y: box.y + offset, width: box.width, height: len };
    place(child, childBox, out);
    offset += len;
  });
}

export function shareOut(sizes: (Size | undefined)[], total: number): number[] {
  const fixed = sizes.reduce((sum, s) => sum + (s && 'px' in s ? s.px : 0), 0);
  const weight = sizes.reduce((sum, s) => sum + (s && 'px' in s ? 0 : s?.fr ?? 1), 0);
  const free = Math.max(0, total - fixed);
  return sizes.map((s) => (s && 'px' in s ? s.px : weight === 0 ? 0 : (free * (s?.fr ?? 1)) / weight));
}

/** CSS for a child of a split, matching `shareOut`. */
export function flexOf(size: Size | undefined): { flex: string } {
  if (size && 'px' in size) return { flex: `0 0 ${size.px}px` };
  return { flex: `${size?.fr ?? 1} 1 0` };
}

/** `upper` ends where `lower` begins (canvas px): one sits right on the other, nothing between them. */
export function sitsOn(upper: Box | undefined, lower: Box | undefined): boolean {
  return !!upper && !!lower && Math.abs(upper.y + upper.height - lower.y) < 1;
}
